import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import { auth } from '../utils/api';
import { Eye, EyeOff } from 'lucide-react';
import PublicNav from '../components/PublicNav';
import usePageMeta from '../utils/usePageMeta';
import { resolvePostLoginPath } from '../utils/productWorkspace';

export default function Login() {
  const [searchParams] = useSearchParams();
  const selectedPlan = useMemo(() => {
    const raw = (searchParams.get('plan') || '').toLowerCase().trim();
    return raw === 'club' || raw === 'coach' ? raw : 'coach';
  }, [searchParams]);
  const intendedPath = useMemo(() => {
    const next = searchParams.get('next') || searchParams.get('redirect') || '';
    return next.startsWith('/') && !next.startsWith('//') ? next : '';
  }, [searchParams]);
  const [mode, setMode] = useState(searchParams.get('mode') === 'signup' ? 'signup' : 'signin');
  const [orgName, setOrgName] = useState('');
  const [teamName, setTeamName] = useState('');
  const [teamNameEdited, setTeamNameEdited] = useState(false);
  const [orgType, setOrgType] = useState('Club');
  const [country, setCountry] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const { login, signup } = useAuth();
  const navigate = useNavigate();
  usePageMeta(
    mode === 'signup' ? 'Start Tactmark Post-Match Free Trial' : 'Sign In | Tactmark',
    mode === 'signup' ? 'Create a Post-Match Coach or Club account with a 14-day free trial.' : 'Sign in to your Tactmark product workspace.'
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (mode === 'forgot') {
      if (!email) { setError('Please enter your email address'); return; }
      setSubmitting(true);
      try {
        const res = await auth.forgotPassword(email);
        setMessage(res.message || 'If an account exists for that email, a reset link has been sent.');
      } catch (err) {
        setError(err.message || 'Something went wrong');
      } finally { setSubmitting(false); }
      return;
    }

    if (!email || !password) { setError('Please enter email and password'); return; }

    if (mode === 'signup') {
      if (!orgName) { setError('Please enter your organization name'); return; }
      if (!teamName.trim()) { setError('Please enter a team or squad name'); return; }
      if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
      if (password !== confirmPassword) { setError('Passwords do not match'); return; }
      if (!acceptedTerms) { setError('Please accept the Terms of Service and Privacy Policy'); return; }
      if (password !== password.trim()) {
        setError('Password must not begin or end with a space. Please remove any leading or trailing spaces.');
        return;
      }
    }

    setSubmitting(true);
    try {
      if (mode === 'signup') {
        const org = await signup(orgName, country, email, password, orgType, teamName.trim(), confirmPassword, selectedPlan, acceptedTerms);
        navigate(resolvePostLoginPath(org, { intendedPath, justSignedUp: true }), { state: { justSignedUp: true } });
      } else {
        const org = await login(email, password);
        navigate(resolvePostLoginPath(org, { intendedPath }));
      }
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally { setSubmitting(false); }
  };

  const switchMode = (next) => {
    setMode(next);
    setError('');
    setMessage('');
    setPassword('');
    setConfirmPassword('');
  };

  const isSignup = mode === 'signup';
  const isSignin = mode === 'signin';

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <PublicNav />

      <div className={`flex-1 flex justify-center px-4 ${isSignup ? 'items-start py-8 sm:py-10' : 'items-center py-16'}`}>
        <div className="w-full max-w-md">
          <div className="mb-8">
            <p className="text-navy-600 text-xs font-semibold tracking-widest uppercase mb-2">
              {isSignup ? 'Create Account' : mode === 'forgot' ? 'Reset Password' : 'Sign In'}
            </p>
            <h1 className="font-serif text-2xl text-navy-900">
              {isSignup ? `Set up your ${selectedPlan === 'club' ? 'Club' : 'Coach'} plan` : mode === 'forgot' ? 'Reset your password' : 'Access your account'}
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              {isSignup
                ? 'Tactmark Post-Match self-service signup · 14-day free trial. Federation contracts are provisioned after a demo.'
                : mode === 'forgot'
                ? "Enter your email and we'll send you a reset link"
                : 'Sign in to Post-Match, Federation, or both — routing follows your organization entitlements'}
            </p>
          </div>

          <div className="border border-gray-200 rounded-lg p-8">
            {mode !== 'forgot' && (
              <div className="flex border-b border-gray-200 mb-6 -mt-2">
                <button type="button" onClick={() => switchMode('signin')}
                  className={`flex-1 pb-3 text-sm font-medium border-b-2 transition-colors ${
                    isSignin ? 'border-navy-900 text-navy-900' : 'border-transparent text-gray-400'
                  }`}>Sign In</button>
                <button type="button" onClick={() => switchMode('signup')}
                  className={`flex-1 pb-3 text-sm font-medium border-b-2 transition-colors ${
                    isSignup ? 'border-navy-900 text-navy-900' : 'border-transparent text-gray-400'
                  }`}>Create Account</button>
              </div>
            )}

            {error && (
              <div role="alert" aria-live="assertive" className="bg-red-50 border border-red-200 text-red-700 rounded px-4 py-3 mb-4 text-sm">{error}</div>
            )}
            {message && (
              <div role="status" aria-live="polite" className="bg-green-50 border border-green-200 text-green-700 rounded px-4 py-3 mb-4 text-sm">{message}</div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4" autoComplete="on">
              {isSignup && (
                <>
                  <div>
                    <label className="text-gray-600 text-sm mb-1 block">Organization name</label>
                    <input type="text" value={orgName} autoComplete="organization"
                      onChange={(e) => { setOrgName(e.target.value); if (!teamNameEdited) setTeamName(e.target.value); }}
                      placeholder="e.g. Riverside FC"
                      className="w-full bg-white border border-gray-300 rounded px-4 py-3 text-navy-900 placeholder-gray-400 focus:outline-none focus:border-navy-600 transition-colors" />
                  </div>
                  <div>
                    <label className="text-gray-600 text-sm mb-1 block">Team or squad name</label>
                    <input type="text" value={teamName} autoComplete="off"
                      onChange={(e) => { setTeamName(e.target.value); setTeamNameEdited(true); }}
                      placeholder="e.g. Riverside FC First Team"
                      className="w-full bg-white border border-gray-300 rounded px-4 py-3 text-navy-900 placeholder-gray-400 focus:outline-none focus:border-navy-600 transition-colors" />
                    <p className="text-gray-400 text-xs mt-1">Prefilled from your organization name edit as needed</p>
                  </div>
                  <div>
                    <label className="text-gray-600 text-sm mb-1 block">Organization type</label>
                    <select value={orgType} onChange={(e) => setOrgType(e.target.value)} autoComplete="off"
                      className="w-full bg-white border border-gray-300 rounded px-4 py-3 text-navy-900 focus:outline-none focus:border-navy-600 transition-colors">
                      {['Club','Academy','National team','School / University','Independent coach / Analyst','Other'].map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-gray-600 text-sm mb-1 block">Country</label>
                    <input type="text" value={country} autoComplete="country-name"
                      onChange={(e) => setCountry(e.target.value)}
                      placeholder="e.g. United States"
                      className="w-full bg-white border border-gray-300 rounded px-4 py-3 text-navy-900 placeholder-gray-400 focus:outline-none focus:border-navy-600 transition-colors" />
                  </div>
                </>
              )}

              <div>
                <label className="text-gray-600 text-sm mb-1 block">Email Address</label>
                <input type="email" value={email}
                  autoComplete={isSignup ? 'email' : 'username'}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="coach@team.com"
                  className="w-full bg-white border border-gray-300 rounded px-4 py-3 text-navy-900 placeholder-gray-400 focus:outline-none focus:border-navy-600 transition-colors" />
              </div>

              {mode !== 'forgot' && (
                <div>
                  <label className="text-gray-600 text-sm mb-1 block">Password</label>
                  <div className="relative">
                    <input
                      type={showPass ? 'text' : 'password'}
                      value={password}
                      autoComplete={isSignup ? 'new-password' : 'current-password'}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full bg-white border border-gray-300 rounded px-4 py-3 text-navy-900 placeholder-gray-400 focus:outline-none focus:border-navy-600 transition-colors pr-10"
                    />
                    <button type="button" onClick={() => setShowPass(!showPass)}
                      className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600" tabIndex="-1">
                      {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {isSignup && <p className="text-gray-400 text-xs mt-1">At least 8 characters. Must not begin or end with a space.</p>}
                  {isSignin && (
                    <button type="button" onClick={() => switchMode('forgot')}
                      className="text-navy-600 text-xs mt-1.5 hover:underline">
                      Forgot password?
                    </button>
                  )}
                </div>
              )}

              {isSignup && (
                <div>
                  <label className="text-gray-600 text-sm mb-1 block">Confirm Password</label>
                  <div className="relative">
                    <input
                      type={showConfirmPass ? 'text' : 'password'}
                      value={confirmPassword}
                      autoComplete="new-password"
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      className={`w-full bg-white border rounded px-4 py-3 text-navy-900 placeholder-gray-400 focus:outline-none transition-colors pr-10 ${
                        confirmPassword && confirmPassword !== password
                          ? 'border-red-400 focus:border-red-500'
                          : 'border-gray-300 focus:border-navy-600'
                      }`}
                    />
                    <button type="button" onClick={() => setShowConfirmPass(!showConfirmPass)}
                      className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600" tabIndex="-1">
                      {showConfirmPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {confirmPassword && confirmPassword !== password && (
                    <p className="text-red-500 text-xs mt-1">Passwords do not match</p>
                  )}
                </div>
              )}

              {isSignup && (
                <label className="flex items-start gap-2 text-xs text-gray-600">
                  <input type="checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} className="mt-0.5" />
                  <span>I agree to the <Link to="/terms" className="text-navy-700 underline">Terms of Service</Link> and <Link to="/privacy" className="text-navy-700 underline">Privacy Policy</Link>.</span>
                </label>
              )}

              <button type="submit" disabled={submitting}
                className="w-full bg-navy-900 hover:bg-navy-800 disabled:opacity-50 text-white font-semibold py-3 rounded transition-colors mt-2">
                {submitting
                  ? (isSignup ? 'Creating account...' : mode === 'forgot' ? 'Sending...' : 'Signing in...')
                  : (isSignup ? 'Create Account' : mode === 'forgot' ? 'Send Reset Link' : 'Sign In')}
              </button>

              {mode === 'forgot' && (
                <button type="button" onClick={() => switchMode('signin')}
                  className="w-full text-center text-gray-500 text-sm mt-2 hover:text-navy-900 transition-colors">
                  Back to Sign In
                </button>
              )}
              {isSignup && (
                <p className="text-xs text-gray-500 mt-3">
                  Need Tactmark Federation? Self-service signup creates Post-Match only.{' '}
                  <Link to="/contact?plan=federation&from=signup" className="text-navy-700 underline">Book a Federation demo</Link>
                  {' '}for assisted provisioning.
                </p>
              )}
            </form>
          </div>

          {!isSignup && <p className="text-center text-gray-500 text-xs mt-6">
            By creating an account, you agree to our{' '}
            <Link to="/terms" className="text-navy-700 hover:underline">Terms of Service</Link>
            {' '}and{' '}
            <Link to="/privacy" className="text-navy-700 hover:underline">Privacy Policy</Link>
          </p>}
        </div>
      </div>
    </div>
  );
}
